import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionNcrDetail } from './hooks/useNcr';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  open: 'bg-red-100 text-red-600',
  under_review: 'bg-blue-100 text-blue-600',
  action_required: 'bg-amber-100 text-amber-600',
  rework_in_progress: 'bg-orange-100 text-orange-600',
  pending_verification: 'bg-indigo-100 text-indigo-600',
  verified: 'bg-emerald-100 text-emerald-600',
  closed: 'bg-slate-900 text-white',
  rejected: 'bg-slate-200 text-slate-500',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  open: 'Mới mở (Open)',
  under_review: 'Đang soát xét',
  action_required: 'Yêu cầu xử lý',
  rework_in_progress: 'Đang sửa lỗi (Rework)',
  pending_verification: 'Chờ xác minh',
  verified: 'Đã xác minh',
  closed: 'Đã đóng',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

const dispositionLabels: Record<string, string> = {
  rework: 'Làm lại (Rework)',
  repair: 'Sửa chữa (Repair)',
  replace: 'Thay thế (Replace)',
  accept_as_is: 'Chấp nhận nguyên trạng',
  use_as_is: 'Dùng nguyên trạng',
  reject: 'Loại bỏ (Reject)',
};

export default function NcrDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { ncr: item, isLoading, updateNcr } = useConstructionNcrDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết NCR...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin NCR</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateNcr({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái NCR sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'root_cause', label: 'Nguyên nhân' },
    { value: 'actions', label: 'Hành động xử lý' },
    { value: 'media', label: 'Ảnh & Tài liệu' },
    { value: 'approval', label: 'Phê duyệt' },
    { value: 'rework', label: 'Lệnh Rework' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.title}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.ncr_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-error-warning-line" />
            {item.severity_code.toUpperCase()}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Báo cáo NCR', path: '/construction/ncr' },
        { label: item.ncr_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'open' && (
            <AuthButton module="quality" action="approve" variant="primary" icon="ri-check-line" onClick={() => handleStatusChange('under_review')}>
              Bắt đầu soát xét
            </AuthButton>
          )}
          {item.status_code === 'under_review' && (
            <AuthButton module="quality" action="approve" variant="success" icon="ri-shield-check-line" onClick={() => handleStatusChange('action_required')}>
              Duyệt hướng xử lý
            </AuthButton>
          )}
          {(item.status_code === 'action_required' || item.status_code === 'rework_in_progress') && (
            <AuthButton module="quality" action="create" variant="info" icon="ri-tools-line">
              Tạo lệnh Rework
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-printer-line">In NCR</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Thiệt hại dự kiến" value={item.estimated_cost_impact?.toLocaleString() || 0} icon="ri-money-dollar-circle-line" variant="danger" suffix=" VNĐ" />
                <KPICard title="Chậm trễ dự kiến" value={item.estimated_delay_days || 0} icon="ri-timer-line" variant="warning" suffix=" ngày" />
                <KPICard title="Hướng xử lý" value={dispositionLabels[item.disposition_type || ''] || 'CHƯA CHỌN'} icon="ri-direction-line" variant="info" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-text-snippet text-os-primary" />
                  Mô tả sai lệch kỹ thuật
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  {item.description || 'Không có mô tả chi tiết cho báo cáo không phù hợp này.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-4 pt-6 border-t border-slate-50">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Loại NCR</span>
                    <span className="text-sm font-bold text-slate-700">{item.ncr_type}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.zone?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người phát hiện</span>
                    <span className="text-sm font-bold text-slate-700">{item.detector?.full_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Hạn chót xử lý</span>
                    <span className="text-sm font-bold text-red-600">{item.due_date ? new Date(item.due_date).toLocaleDateString('vi-VN') : 'N/A'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-history-line text-slate-400" />
                  Tiến độ khắc phục
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <Avatar name={item.detector?.full_name} size="xs" />
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold ">Phát hiện bởi (QA/QC)</p>
                        <p className="text-xs font-bold text-slate-700">{item.detector?.full_name}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400">{new Date(item.detected_at).toLocaleString('vi-VN')}</span>
                  </div>
                  {item.resolved_at && (
                    <div className="flex items-center justify-between p-3 bg-os-primary/5 rounded-2xl border border-os-primary/10">
                      <div className="flex items-center gap-3">
                        <Avatar name={item.assignee?.full_name} size="xs" />
                        <div>
                          <p className="text-[10px] text-os-primary font-bold ">Xử lý xong bởi</p>
                          <p className="text-xs font-bold text-slate-700">{item.assignee?.full_name}</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-400">{new Date(item.resolved_at).toLocaleString('vi-VN')}</span>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-user-settings-line" />
                  Trách nhiệm giải trình
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <Avatar name={item.assignee?.full_name} src={item.assignee?.avatar_url} size="sm" />
                    <div className="flex flex-col">
                      <p className="text-[10px] text-slate-400 font-bold ">Người chịu trách nhiệm</p>
                      <p className="text-sm font-bold text-slate-200">{item.assignee?.full_name || 'Đang chờ gán...'}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Tổ đội thi công lỗi</p>
                    <p className="text-sm font-bold text-slate-200">{item.responsible_team?.name || '---'}</p>
                  </div>
                </div>
              </Card>

              {item.corrective_action && (
                <Alert variant="info" title="Hành động khắc phục" className="text-xs">
                  {item.corrective_action}
                </Alert>
              )}

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-slate-400" />
                  Chứng từ tham chiếu
                </h4>
                <div className="space-y-2">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-shield-check-line">Biên bản QA: {item.qa_inspection_id?.substring(0, 8)}</Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-file-list-line">Lệnh thi công: {item.work_order_id?.substring(0, 8)}</Button>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-xs" icon="ri-file-paper-line">Tiêu chuẩn Spec dự án</Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'root_cause' || activeTab === 'actions' || activeTab === 'media' || activeTab === 'approval' || activeTab === 'rework' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ phân tích nguyên nhân kỹ thuật, hồ sơ pháp lý về chất lượng và các hướng xử lý (Disposition) đã được phê duyệt."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
